import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { useLocation } from 'react-router';

export const KNOWLEDGE_BASE_CONFIG = {
  baseUrl: 'https://www.hibambi.com/knowledge-base',
  pathMap: {
    billing: 'billing',
    dispatch: 'quick-start-guide',
    fleet: 'fleet',
    'live-map': 'map',
    passengers: 'passengers',
    payers: 'adding-payers',
    'payers/pricing': 'pricing',
    reports: 'reports',
    settings: 'organizational-settings',
    team: 'user-accounts',
    trips: 'trips',
  } as const,
} as const;

type RouteKey = keyof typeof KNOWLEDGE_BASE_CONFIG.pathMap;

export default function KnowledgeBaseIconLink() {
  const location = useLocation();

  // Pricing is a tab under Payers with its own article, so a two-segment
  // match wins over the section it lives in.
  const route = useMemo((): RouteKey | undefined => {
    const [, section, tab] = location.pathname.split('/');

    if (`${section}/${tab}` in KNOWLEDGE_BASE_CONFIG.pathMap) {
      return `${section}/${tab}` as RouteKey;
    }

    return section in KNOWLEDGE_BASE_CONFIG.pathMap
      ? (section as RouteKey)
      : undefined;
  }, [location.pathname]);

  const handleClick = () => {
    const path = route ? KNOWLEDGE_BASE_CONFIG.pathMap[route] : undefined;
    const url = path
      ? `${KNOWLEDGE_BASE_CONFIG.baseUrl}/${path}`
      : KNOWLEDGE_BASE_CONFIG.baseUrl;

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const ariaLabel = `Open knowledge base${
    route ? ` for ${route} section` : ''
  }`;

  return (
    <SimpleTooltip
      trigger={
        <button
          type="button"
          className="focus:ring-indigo-500 rounded-full p-1 text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2"
          data-testid="knowledge-base-trigger"
          aria-label={ariaLabel}
          onClick={handleClick}
        >
          <span className="sr-only">Knowledge base</span>
          <InformationCircleIcon
            data-testid="knowledge-base-icon"
            className="h-7 w-7"
            aria-hidden="true"
          />
        </button>
      }
      content={
        <div className="text-sm">
          Got questions? Click the icon to visit our Knowledge Base for answers
          to common questions.
        </div>
      }
    />
  );
}
